import type { CachedRecentSearch } from '@/redux/modules/search/save/saveSearch.api';
import type { SearchParams } from '@/types/SaveSearch';
import type { Facet } from '@/types/SearchFacet';

export type SavedSearch = {
    createdDate?: string;
    facets: Facet[];
    itemImageURL: string;
    lastProcessed: string;
    lastSent: string;
    lastStatus: string;
    resultCount?: number;
    savedSearchId: number;
    searchURL: string;
    sendAlert: boolean;
    title?: string;
    userId: number;
};

export enum SaveSearchResult {
    Failed = 'failed',
    InvalidFacets = 'invalidFacets',
    Saved = 'saved',
}

type SaveSearchToast = {
    description: string;
    title: string;
};

/**
 * Toasts a follow shows once its outcome is known. Each surface words its confirmation differently, so
 * callers pass the copy; an outcome without one shows nothing.
 */
export type SaveSearchFeedback = {
    failed?: SaveSearchToast;
    invalidFacets?: SaveSearchToast;
    saved?: SaveSearchToast;
};

/**
 * Toasts an unfollow shows once its outcome is known; an outcome without one shows nothing.
 */
export type DeleteSavedSearchFeedback = {
    failed?: SaveSearchToast;
    removed?: SaveSearchToast;
};

/**
 * An unfollow the API has confirmed. `unfollowedAt` is when the confirmation arrived, not when the request
 * was sent: a list requested while the delete was in flight can still contain the search.
 */
export type ConfirmedUnfollow = {
    savedSearchId: number;
    unfollowedAt: number;
};

export type DeleteSavedSearchResult = ConfirmedUnfollow & {
    key: string;
};

type FollowedSearch = {
    deleted: boolean;
    key: string;
    savedSearchId: number;
};

export type SaveSearchSlice = {
    /**
     * Any entry that is not `deleted` reads as followed, so a failed save must never write one.
     */
    byParams: { [key: string]: FollowedSearch };
    error: boolean;
    newSignupSearchParams: SearchParams;
    recentSearches: CachedRecentSearch[];
    recentSearchesLoaded: number;
    recentSearchesLoading: boolean;
    //TODO remove these and update places using them to use byParams
    submitted: boolean;
    success: boolean;
    /**
     * When each unfollow was confirmed, by saved search id, so a list response requested earlier cannot
     * mark the search followed again. Keyed by id rather than params because following again creates a
     * new id, and the email unfollow link knows only the id.
     */
    unfollowedAt: Partial<Record<number, number>>;
};

export const defaultSaveSearchSlice: SaveSearchSlice = {
    byParams: {},
    error: false,
    newSignupSearchParams: {},
    recentSearches: [],
    recentSearchesLoaded: 0,
    recentSearchesLoading: false,
    submitted: false,
    success: false,
    unfollowedAt: {},
};
